import { ui } from "./ui";
import { talkBrand } from "./brand";
import type {
  ApiProblem,
  AuthSnapshot,
  CreatedRoomResult,
  JoinRoomResult,
  ParticipantIdentity,
  RegistrationChallenge,
  RoomHistoryItem,
  RoomResolution,
  RoomMode,
  RoomState,
  RTCBackend,
  RTCBinding,
  RTCCredentials,
  TalkProfile,
  TalkRoom,
  PrivateCallMediaSecurity,
} from "./types";
import { isRosterAssetKey, type RosterAssetKey } from "./assets";
import { normalizeRoomCode } from "./navigation";
import { parseMediaPolicy } from "./mediaPolicy";
import { avatarUploadContentType, customAvatarURL } from "./avatar";

const API_ROOT = import.meta.env.VITE_API_ROOT ?? "/api/v1";
const PRIVATE_PROTOCOL_HEADERS = { "X-Talk-Private-Protocol": "ctfonly-private-v1" } as const;

interface WireRoom {
  id: string;
  mode?: unknown;
  title: string;
  state: string;
  rtcBackend?: unknown;
  rtcEpoch?: unknown;
  allowGuests: boolean;
  haramModeDefault?: unknown;
  maxParticipants?: number;
  participantLimit?: number;
  participantCount?: number;
  ownerId?: string;
  createdAt?: string;
  scheduledAt?: string;
  scheduledTimeZone?: string;
  earlyEntryAt?: string;
  openedAt?: string;
  endedAt?: string;
}

interface WireRTC {
  token: string;
  serverUrl: string;
  roomName?: unknown;
  backend?: unknown;
  epoch?: unknown;
  expiresAt?: string;
}

interface WireParticipant {
  id: string;
  identity: string;
  displayName: string;
  role: string;
  guest: boolean;
  assetKey?: unknown;
  avatarUrl?: string;
}

interface WireJoinRoomResult {
  room: WireRoom;
  participant: WireParticipant;
  participantTicket: string;
  rtc: WireRTC;
  mediaPolicy?: unknown;
  mediaSecurity?: unknown;
}

function roomModeFromWire(value: unknown): RoomMode {
  if (value === undefined || value === null || value === "") return "standard";
  if (value === "standard" || value === "private_1to1") return value;
  throw new Error("The server returned an unknown room mode.");
}

const stunURLPattern = /^(stuns?):(\[[0-9A-Fa-f:.]+\]|[A-Za-z0-9.-]+)(?::([0-9]{1,5}))?$/;

function isSafeSTUNURL(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 255) return false;
  const match = stunURLPattern.exec(value);
  if (!match) return false;
  const host = match[2] ?? "";
  if (host.startsWith("[")) {
    if (!host.includes(":") || host === "[]") return false;
    try {
      if (!new URL(`https://${host}`).hostname.startsWith("[")) return false;
    } catch {
      return false;
    }
  } else if (host.length > 253 || host.split(".").some((label) => label.length < 1 || label.length > 63 || label.startsWith("-") || label.endsWith("-"))) {
    return false;
  }
  if (match[3]) {
    const port = Number(match[3]);
    if (!Number.isInteger(port) || port < 1 || port > 65_535) return false;
  }
  return true;
}

function privateMediaSecurityFromWire(value: unknown): PrivateCallMediaSecurity {
  if (!value || typeof value !== "object") throw new Error("The private call security policy is missing.");
  const policy = value as Record<string, unknown>;
  if (
    policy.mode !== "private_1to1"
    || policy.protocol !== "ctfonly-private-v1"
    || policy.e2eeRequired !== true
    || policy.topology !== "p2p_preferred"
    || policy.fallback !== "livekit_e2ee"
    || typeof policy.directTimeoutMs !== "number"
    || !Number.isInteger(policy.directTimeoutMs)
    || policy.directTimeoutMs < 1_000
    || policy.directTimeoutMs > 15_000
    || !Array.isArray(policy.iceServers)
    || policy.iceServers.length < 1
    || policy.iceServers.length > 4
  ) {
    throw new Error("The private call security policy is invalid.");
  }
  let urlCount = 0;
  const iceServers = policy.iceServers.map((candidate) => {
    if (!candidate || typeof candidate !== "object") throw new Error("The private call ICE policy is invalid.");
    const urls = (candidate as { urls?: unknown }).urls;
    const normalized = typeof urls === "string" ? [urls] : Array.isArray(urls) ? urls : [];
    urlCount += normalized.length;
    if (normalized.length < 1 || urlCount > 4 || normalized.some((url) => !isSafeSTUNURL(url))) {
      throw new Error("The private call ICE policy must contain STUN servers only.");
    }
    return { urls: normalized } satisfies RTCIceServer;
  });
  return {
    mode: "private_1to1",
    protocol: "ctfonly-private-v1",
    e2eeRequired: true,
    topology: "p2p_preferred",
    fallback: "livekit_e2ee",
    directTimeoutMs: policy.directTimeoutMs,
    iceServers,
  };
}

function roomStateFromWire(value: string): RoomState {
  if (["scheduled", "opening", "open", "locked", "ending", "ended", "failed"].includes(value)) return value as RoomState;
  throw new Error("The server returned an unknown room state.");
}

function rtcBackendFromWire(value: unknown): RTCBackend {
  if (value === "primary" || value === "fallback") return value;
  throw new Error("The server returned an unknown RTC backend.");
}

function rtcEpochFromWire(value: unknown): number {
  if (typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 1_000_000) return value;
  throw new Error("The server returned an invalid RTC epoch.");
}

function expectedRTCRoomName(roomId: string, epoch: number): string {
  const compact = roomId.toLowerCase().replaceAll("-", "");
  if (!/^[0-9a-f]{32}$/u.test(compact)) throw new Error("The server returned an invalid room identity.");
  return `talk_${compact}${epoch === 0 ? "" : `_e${epoch}`}`;
}

function rtcServerURLFromWire(value: unknown): string {
  if (typeof value !== "string" || value.length < 1 || value.length > 2048) throw new Error("The server returned an invalid RTC server URL.");
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error("The server returned an invalid RTC server URL.");
  }
  const developmentScheme = import.meta.env.DEV && (parsed.protocol === "ws:" || parsed.protocol === "http:");
  if ((parsed.protocol !== "wss:" && parsed.protocol !== "https:" && !developmentScheme) || !parsed.hostname || parsed.username || parsed.password || parsed.hash) {
    throw new Error("The server returned an unsafe RTC server URL.");
  }
  return value;
}

function rtcBindingFromWire(value: WireRTC, roomId: string): RTCBinding {
  if (!value || typeof value !== "object") throw new Error("The server returned incomplete RTC credentials.");
  const backend = rtcBackendFromWire(value.backend);
  const epoch = rtcEpochFromWire(value.epoch);
  const roomName = value.roomName;
  if (typeof roomName !== "string" || roomName !== expectedRTCRoomName(roomId, epoch)) {
    throw new Error("The server returned RTC credentials for a different room epoch.");
  }
  return { backend, epoch, roomName, serverUrl: rtcServerURLFromWire(value.serverUrl) };
}

function rtcCredentialsFromWire(value: WireRTC, roomId: string): RTCCredentials {
  const binding = rtcBindingFromWire(value, roomId);
  if (typeof value.token !== "string" || !value.token) throw new Error("The server did not issue an RTC token.");
  return { ...binding, token: value.token, ...(typeof value.expiresAt === "string" ? { expiresAt: value.expiresAt } : {}) };
}

function participantFromWire(value: WireParticipant): ParticipantIdentity {
  if (!value || typeof value.id !== "string" || !value.id || typeof value.identity !== "string" || !value.identity || typeof value.displayName !== "string") {
    throw new Error("The server returned an incomplete participant identity.");
  }
  if (value.role !== "host" && value.role !== "cohost" && value.role !== "participant") throw new Error("The server returned an unknown participant role.");
  return {
    id: value.id,
    identity: value.identity,
    displayName: value.displayName,
    role: value.role,
    guest: value.guest === true,
    ...(isRosterAssetKey(value.assetKey) ? { assetKey: value.assetKey } : {}),
    ...(customAvatarURL(value.avatarUrl) ? { avatarUrl: customAvatarURL(value.avatarUrl) } : {}),
  };
}

function profileFromWire(value: unknown): TalkProfile {
  if (!value || typeof value !== "object") throw new Error("The server returned an invalid Talk profile.");
  const profile = (value as { profile?: unknown }).profile;
  if (!profile || typeof profile !== "object") throw new Error("The server returned an invalid Talk profile.");
  const assetKey = (profile as { assetKey?: unknown }).assetKey;
  if (!isRosterAssetKey(assetKey)) throw new Error("The server returned an unknown Talk avatar.");
  const avatarUrl = customAvatarURL((profile as { avatarUrl?: unknown }).avatarUrl);
  return { assetKey, ...(avatarUrl ? { avatarUrl } : {}) };
}

function roomFromWire(value: WireRoom, code?: string): TalkRoom {
  const participantLimit = value.maxParticipants ?? value.participantLimit;
  if (!value || typeof value.id !== "string" || !value.id || typeof value.title !== "string" || typeof value.allowGuests !== "boolean" || typeof participantLimit !== "number" || participantLimit < 2 || participantLimit > 50) {
    throw new Error(ui("The server returned an incomplete room.", "Сервер вернул неполные данные комнаты."));
  }
  const rtcBackend = rtcBackendFromWire(value.rtcBackend);
  const rtcEpoch = rtcEpochFromWire(value.rtcEpoch);
  return {
    id: value.id,
    ...(code ? { code } : {}),
    mode: roomModeFromWire(value.mode),
    title: value.title,
    state: roomStateFromWire(value.state),
    rtcBackend,
    rtcEpoch,
    allowGuests: value.allowGuests,
    haramModeDefault: value.haramModeDefault === true,
    participantCount: value.participantCount ?? 0,
    participantLimit,
    ownerId: value.ownerId,
    createdAt: value.createdAt,
    scheduledAt: value.scheduledAt,
    scheduledTimeZone: value.scheduledTimeZone,
    earlyEntryAt: value.earlyEntryAt,
    openedAt: value.openedAt,
    endedAt: value.endedAt,
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields?: Record<string, string>;
  readonly requestId?: string;

  constructor(status: number, problem: ApiProblem) {
    super(problem.message);
    this.name = "ApiError";
    this.status = status;
    this.code = problem.code;
    this.fields = problem.fields;
    this.requestId = problem.requestId;
  }
}

class ApiClient {
  private csrfToken = "";
  private csrfRefresh: Promise<void> | null = null;

  setCsrfToken(token: string): void {
    this.csrfToken = token;
  }

  clearCsrfToken(): void {
    this.csrfToken = "";
  }

  private refreshCsrfToken(signal?: AbortSignal): Promise<void> {
    if (this.csrfRefresh) return this.csrfRefresh;
    this.csrfRefresh = (async () => {
      try {
        const timeout = AbortSignal.timeout(15_000);
        const requestSignal = signal ? AbortSignal.any([signal, timeout]) : timeout;
        const response = await fetch(`${API_ROOT}/auth/me`, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: requestSignal,
        });
        if (!response.ok) return;
        const snapshot = await response.json() as Partial<AuthSnapshot>;
        if (typeof snapshot.csrfToken === "string" && snapshot.csrfToken) this.setCsrfToken(snapshot.csrfToken);
      } catch {
        // The original request remains authoritative; callers receive its
        // typed error if the account authority cannot refresh CSRF state.
      } finally {
        this.csrfRefresh = null;
      }
    })();
    return this.csrfRefresh;
  }

  async request<T>(path: string, init: RequestInit = {}, retryCsrf = true): Promise<T> {
    const method = (init.method ?? "GET").toUpperCase();
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    headers.set("X-Request-ID", crypto.randomUUID());
    if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    if (!(["GET", "HEAD", "OPTIONS"].includes(method)) && this.csrfToken) headers.set("X-CSRF-Token", this.csrfToken);

    const timeout = AbortSignal.timeout(15_000);
    const signal = init.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
    const response = await fetch(`${API_ROOT}${path}`, {
      ...init,
      method,
      headers,
      credentials: "include",
      signal,
    });

    if (!response.ok) {
      let problem: ApiProblem = {
        code: "request_failed",
        message: response.status >= 500
          ? "Talk could not complete that request. Try again."
          : "That request could not be completed.",
      };
      try {
        problem = { ...problem, ...(await response.json() as Partial<ApiProblem>) };
      } catch {
        // The safe generic problem remains.
      }
      if (retryCsrf && !["GET", "HEAD", "OPTIONS"].includes(method) && problem.code === "csrf_rejected") {
        await this.refreshCsrfToken(init.signal ?? undefined);
        if (this.csrfToken) return this.request<T>(path, init, false);
      }
      throw new ApiError(response.status, problem);
    }

    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  get<T>(path: string, signal?: AbortSignal, headers?: HeadersInit): Promise<T> {
    return this.request(path, { ...(signal ? { signal } : {}), ...(headers ? { headers } : {}) });
  }

  post<T>(path: string, body?: unknown, signal?: AbortSignal, headers?: HeadersInit): Promise<T> {
    return this.request(path, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
      ...(signal ? { signal } : {}),
      ...(headers ? { headers } : {}),
    });
  }

  patch<T>(path: string, body: unknown): Promise<T> {
    return this.request(path, { method: "PATCH", body: JSON.stringify(body) });
  }

  delete<T>(path: string, body?: unknown): Promise<T> {
    return this.request(path, { method: "DELETE", body: body === undefined ? undefined : JSON.stringify(body) });
  }
}

export const apiClient = new ApiClient();

export const api = {
  auth: {
    me: (signal?: AbortSignal) => apiClient.get<AuthSnapshot>("/auth/me", signal),
    login: (input: { identifier: string; password: string }) =>
      apiClient.post<AuthSnapshot>("/auth/login", input),
    logout: () => apiClient.post<void>("/auth/logout"),
  },
  profile: {
    load: async (signal?: AbortSignal): Promise<TalkProfile> => profileFromWire(await apiClient.post<unknown>("/talk/profile", undefined, signal)),
    update: async (assetKey: RosterAssetKey): Promise<TalkProfile> => {
      if (!isRosterAssetKey(assetKey)) throw new Error("Choose an approved Talk avatar.");
      return profileFromWire(await apiClient.patch<unknown>("/talk/profile", { assetKey }));
    },
    upload: async (file: File): Promise<TalkProfile> => {
      const contentType = avatarUploadContentType(file);
      if (file.size < 1 || file.size > 2 * 1024 * 1024 || !contentType) {
        throw new Error("Choose a JPG or PNG image no larger than 2 MB.");
      }
      return profileFromWire(await apiClient.request<unknown>("/talk/profile/avatar", {
        method: "POST", body: file, headers: { "Content-Type": contentType },
      }));
    },
  },
  registration: {
    challenge: (handle: string, signal?: AbortSignal) =>
      apiClient.post<RegistrationChallenge>("/accounts/registration-challenges", { handle }, signal),
    cancel: (id: string) => apiClient.delete<void>(`/accounts/registration-challenges/${encodeURIComponent(id)}`),
    register: (input: { handle: string; password: string; challengeId: string; nonce: string }) =>
      apiClient.post<AuthSnapshot>("/accounts/register", input),
    registerDirect: (input: { handle: string; password: string; displayName?: string }) =>
      apiClient.post<AuthSnapshot>("/accounts/register", input),
  },
  rooms: {
    create: async (input: { title: string; allowGuests: boolean; haramModeDefault: boolean; participantLimit: number; mode?: RoomMode; scheduledAt?: string; timeZone?: string }): Promise<CreatedRoomResult> => {
      const result = await apiClient.post<{ room: WireRoom; code: string; prejoinTicket?: string; prejoinExpiresAt?: string }>("/talk/rooms", {
        title: input.title, allowGuests: input.allowGuests, haramModeDefault: input.haramModeDefault, maxParticipants: input.participantLimit,
        ...(input.mode ? { mode: input.mode } : {}),
        ...(input.scheduledAt ? { scheduledAt: input.scheduledAt, timeZone: input.timeZone } : {}),
      });
      return { ...result, room: roomFromWire(result.room, result.code) };
    },
    get: async (id: string, signal?: AbortSignal, participantTicket?: string): Promise<TalkRoom> => {
      const result = await apiClient.get<{ room: WireRoom }>(`/talk/rooms/${encodeURIComponent(id)}`, signal, participantTicket ? { "X-Participant-Ticket": participantTicket } : undefined);
      return roomFromWire(result.room);
    },
    update: async (id: string, input: Partial<Pick<TalkRoom, "title" | "allowGuests" | "haramModeDefault" | "scheduledAt" | "scheduledTimeZone">>): Promise<TalkRoom> => {
      const result = await apiClient.patch<{ room: WireRoom }>(`/talk/rooms/${encodeURIComponent(id)}`, {
        ...(input.title !== undefined ? { title: input.title } : {}),
        ...(input.allowGuests !== undefined ? { allowGuests: input.allowGuests } : {}),
        ...(input.haramModeDefault !== undefined ? { haramModeDefault: input.haramModeDefault } : {}),
        ...(input.scheduledAt !== undefined ? { scheduledAt: input.scheduledAt } : {}),
        ...(input.scheduledTimeZone !== undefined ? { timeZone: input.scheduledTimeZone } : {}),
      });
      return roomFromWire(result.room);
    },
    resolve: async (code: string, signal?: AbortSignal): Promise<RoomResolution> => {
      const result = await apiClient.post<{ room: WireRoom; prejoinTicket: string; prejoinExpiresAt?: string }>("/talk/rooms/resolve", { code }, signal);
      return { room: roomFromWire(result.room, code), prejoinTicket: result.prejoinTicket, expiresAt: result.prejoinExpiresAt };
    },
    accountPrejoin: async (id: string, signal?: AbortSignal): Promise<RoomResolution> => {
      const result = await apiClient.post<{ room: WireRoom; prejoinTicket: string; prejoinExpiresAt?: string }>(`/talk/rooms/${encodeURIComponent(id)}/prejoin`, undefined, signal);
      return { room: roomFromWire(result.room), prejoinTicket: result.prejoinTicket, expiresAt: result.prejoinExpiresAt };
    },
    join: async (id: string, input: { prejoinTicket: string; displayName: string; assetKey?: RosterAssetKey }): Promise<JoinRoomResult> => {
      if (input.assetKey !== undefined && !isRosterAssetKey(input.assetKey)) throw new Error("Choose an approved Talk avatar.");
      const result = await apiClient.post<WireJoinRoomResult>(`/talk/rooms/${encodeURIComponent(id)}/join`, input, undefined, PRIVATE_PROTOCOL_HEADERS);
      const room = roomFromWire(result.room);
      const rtcBinding = rtcBindingFromWire(result.rtc, room.id);
      if (rtcBinding.backend !== room.rtcBackend || rtcBinding.epoch !== room.rtcEpoch) throw new Error("The room and RTC credentials belong to different epochs.");
      return {
        room, participant: participantFromWire(result.participant),
        participantTicket: result.participantTicket, rtcToken: room.mode === "private_1to1" ? "" : result.rtc.token,
        serverUrl: rtcBinding.serverUrl, rtcBinding, rtcExpiresAt: result.rtc.expiresAt,
        mediaPolicy: parseMediaPolicy(result.mediaPolicy),
        ...(room.mode === "private_1to1" ? { mediaSecurity: privateMediaSecurityFromWire(result.mediaSecurity) } : {}),
      };
    },
    reconnect: async (id: string, signal?: AbortSignal): Promise<JoinRoomResult> => {
      const result = await apiClient.post<WireJoinRoomResult>(`/talk/rooms/${encodeURIComponent(id)}/reconnect`, undefined, signal, PRIVATE_PROTOCOL_HEADERS);
      const room = roomFromWire(result.room);
      const rtcBinding = rtcBindingFromWire(result.rtc, room.id);
      if (rtcBinding.backend !== room.rtcBackend || rtcBinding.epoch !== room.rtcEpoch) throw new Error("The room and RTC credentials belong to different epochs.");
      return {
        room, participant: participantFromWire(result.participant),
        participantTicket: result.participantTicket, rtcToken: room.mode === "private_1to1" ? "" : result.rtc.token,
        serverUrl: rtcBinding.serverUrl, rtcBinding, rtcExpiresAt: result.rtc.expiresAt,
        mediaPolicy: parseMediaPolicy(result.mediaPolicy),
        ...(room.mode === "private_1to1" ? { mediaSecurity: privateMediaSecurityFromWire(result.mediaSecurity) } : {}),
      };
    },
    invitationCode: async (id: string): Promise<string> => {
      const result = await apiClient.post<{ code?: unknown }>(`/talk/rooms/${encodeURIComponent(id)}/invitation-code`);
      const code = normalizeRoomCode(typeof result.code === "string" ? result.code : "");
      if (code.replaceAll("-", "").length !== 12) throw new Error("The server returned an invalid invitation code.");
      return code;
    },
    cancel: async (id: string): Promise<TalkRoom> => {
      const result = await apiClient.post<{ room: WireRoom }>(`/talk/rooms/${encodeURIComponent(id)}/cancel`);
      return roomFromWire(result.room);
    },
    refreshRtc: async (id: string, participantTicket: string): Promise<RTCCredentials> =>
      rtcCredentialsFromWire(await apiClient.post<WireRTC>(`/talk/rooms/${encodeURIComponent(id)}/rtc-token`, { participantTicket }, undefined, PRIVATE_PROTOCOL_HEADERS), id),
    leave: (id: string, participantTicket: string) => apiClient.post<void>(`/talk/rooms/${encodeURIComponent(id)}/leave`, { participantTicket }),
    end: async (id: string): Promise<TalkRoom> => {
      const result = await apiClient.post<{ room: WireRoom }>(`/talk/rooms/${encodeURIComponent(id)}/end`);
      return roomFromWire(result.room);
    },
    lock: async (id: string, locked: boolean): Promise<TalkRoom> => {
      const result = await apiClient.post<{ room: WireRoom }>(`/talk/rooms/${encodeURIComponent(id)}/${locked ? "lock" : "unlock"}`);
      return roomFromWire(result.room);
    },
    removeParticipant: (id: string, participantId: string) =>
      apiClient.delete<void>(`/talk/rooms/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}`, { reason: "Removed by host" }),
    muteParticipant: (id: string, participantId: string) =>
      apiClient.post<void>(`/talk/rooms/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}/mute`, { source: "microphone" }),
    requestUnmute: (id: string, participantId: string) =>
      apiClient.post<void>(`/talk/rooms/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}/request-unmute`, { source: "microphone" }),
    stopTrack: (id: string, participantId: string, source: "camera" | "screen_share") =>
      apiClient.post<void>(`/talk/rooms/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}/stop-track`, { source }),
    setParticipantRole: async (id: string, participantId: string, role: "cohost" | "participant"): Promise<ParticipantIdentity> => {
      const result = await apiClient.patch<{ participant: WireParticipant }>(`/talk/rooms/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}/role`, { role });
      return participantFromWire(result.participant);
    },
    transferHost: async (id: string, participantId: string): Promise<{ room: TalkRoom; participants: ParticipantIdentity[] }> => {
      const result = await apiClient.post<{ room: WireRoom; participants: WireParticipant[] }>(`/talk/rooms/${encodeURIComponent(id)}/participants/${encodeURIComponent(participantId)}/transfer-host`);
      return { room: roomFromWire(result.room), participants: result.participants.map(participantFromWire) };
    },
    upcoming: async (signal?: AbortSignal): Promise<{ items: RoomHistoryItem[] }> => {
      const result = await apiClient.get<{ items: unknown[] }>("/talk/rooms/upcoming", signal);
      const items = Array.isArray(result.items) ? result.items.flatMap((value) => {
        if (!value || typeof value !== "object") return [];
        const item = value as Record<string, unknown>;
        const wireRoom = (item.room && typeof item.room === "object" ? item.room : item) as WireRoom;
        try {
          return [{ room: roomFromWire(wireRoom), role: item.role === "host" ? "host" as const : item.role === "cohost" ? "cohost" as const : "participant" as const }];
        } catch {
          return [];
        }
      }) : [];
      return { items };
    },
    history: async (signal?: AbortSignal): Promise<{ items: RoomHistoryItem[] }> => {
      const result = await apiClient.get<{ items: unknown[] }>("/talk/history", signal);
      const items = Array.isArray(result.items) ? result.items.flatMap((value) => {
        if (!value || typeof value !== "object") return [];
        const item = value as Record<string, unknown>;
        const wireRoom = (item.room && typeof item.room === "object" ? item.room : item) as WireRoom;
        try {
          return [{
            room: roomFromWire(wireRoom),
            role: item.role === "host" ? "host" as const : item.role === "cohost" ? "cohost" as const : "participant" as const,
            joinedAt: typeof item.joinedAt === "string" ? item.joinedAt : undefined,
            durationSeconds: typeof item.durationSeconds === "number" ? item.durationSeconds : undefined,
          }];
        } catch {
          return [];
        }
      }) : [];
      return { items };
    },
  },
};

const RUSSIAN_PROBLEMS: Record<string, string> = {
  authentication_required: "Войдите, чтобы продолжить.",
  unauthenticated: "Войдите, чтобы продолжить.",
  forbidden: "Это действие недоступно.",
  room_not_found: "Комната или код не найдены.",
  invalid_ticket: "Снова введите код комнаты, прежде чем входить.",
  guest_session_missing: "Снова введите код комнаты, прежде чем входить.",
  room_not_started: "Комната ещё не началась.",
  room_locked: "Комната закрыта.",
  room_ended: "Комната завершена.",
  room_full: "В комнате больше нет мест.",
  state_conflict: "Состояние комнаты изменилось. Обновите страницу и попробуйте снова.",
  guests_disabled: "В эту комнату можно войти только с аккаунтом.",
  avatar_asset_invalid: "Выберите шахматную фигуру из списка.",
  avatar_too_large: "Изображение должно быть не больше 2 МБ.",
  avatar_image_invalid: "Нужен обычный JPG или PNG от 64 до 4096 пикселей.",
  participant_offline: "Участник сейчас не подключён к звонку.",
  track_not_found: "Эта дорожка участника не найдена.",
  account_service_unavailable: "Проверка аккаунта временно недоступна.",
  internal_error: "Запрос не выполнился. Попробуйте ещё раз.",
  request_failed: "Запрос не выполнился. Попробуйте ещё раз.",
  csrf_rejected: "Защитный ключ устарел. Обновите страницу.",
  origin_rejected: "Этот адрес не может отправлять такой запрос.",
  rate_limited: "Слишком много попыток. Попробуйте позже.",
  invalid_credentials: "Неверное имя или пароль.",
  handle_taken: "Такое имя аккаунта уже занято.",
  invalid_account: "Проверьте данные аккаунта.",
  invalid_json: "Запрос составлен неверно.",
  connection_limit: "Слишком много подключений к комнате.",
  not_ready: "Нужная служба сейчас недоступна.",
  role_invalid: "Можно назначить соведущего или участника.",
  participant_not_found: "Участник не найден.",
  participant_event_unavailable: "Этому участнику сейчас нельзя отправить просьбу включить микрофон.",
  source_invalid: "Просьба включить звук относится только к микрофону.",
  track_selector_invalid: "Выберите камеру или экран.",
  content_type_required: "Нужен запрос в формате JSON.",
  body_too_large: "Запрос слишком большой.",
};

const RUSSIAN_INPUTS: Record<string, string> = {
  "room title must contain 1 to 100 characters": "Название комнаты — от 1 до 100 символов.",
  "room mode is unsupported": "Такой режим комнаты не поддерживается.",
  "private 1:1 rooms must have exactly two participants": "В личном звонке ровно два участника.",
  "scheduled time must be at least two minutes in the future": "Время должно быть хотя бы на две минуты позже текущего.",
  "scheduled time must be within one year": "Время должно быть не дальше чем через год.",
  "choose a valid IANA time zone": "Выберите настоящий часовой пояс.",
  "at least one room field is required": "Нужно изменить хотя бы одно поле.",
  "scheduled time and time zone must be updated together": "Время и часовой пояс меняются вместе.",
};

export function getApiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (talkBrand.id === "chess") {
      return RUSSIAN_PROBLEMS[error.code] ?? RUSSIAN_INPUTS[error.message] ?? error.message;
    }
    return error.message;
  }
  if (error instanceof DOMException && (error.name === "TimeoutError" || error.name === "AbortError")) {
    return error.name === "TimeoutError" ? ui("Talk took too long to respond. Try again.", "Ответ занял слишком много времени. Попробуйте снова.") : ui("The request was cancelled.", "Запрос отменён.");
  }
  if (error instanceof TypeError) return ui("Talk is unreachable. Check your connection and try again.", "Сервер недоступен. Проверьте соединение и попробуйте снова.");
  return ui("Something went wrong. Try again.", "Что-то пошло не так. Попробуйте снова.");
}

export function assertRoom(value: TalkRoom): TalkRoom {
  if (!value || typeof value.id !== "string" || !value.id || typeof value.title !== "string" || !value.participantLimit || (value.rtcBackend !== "primary" && value.rtcBackend !== "fallback") || !Number.isInteger(value.rtcEpoch) || value.rtcEpoch < 0) {
    throw new Error(ui("The server returned an incomplete room.", "Сервер вернул неполные данные комнаты."));
  }
  return value;
}

export function assertRoomResolution(value: RoomResolution): RoomResolution {
  assertRoom(value?.room);
  if (typeof value.prejoinTicket !== "string" || !value.prejoinTicket) throw new Error(ui("The server did not issue a prejoin ticket.", "Сервер не выдал допуск в комнату."));
  return value;
}

export function assertJoinResult(value: JoinRoomResult): JoinRoomResult {
  assertRoom(value?.room);
  if (typeof value.participantTicket !== "string" || !value.participantTicket || typeof value.rtcToken !== "string" || (value.room.mode !== "private_1to1" && !value.rtcToken) || typeof value.serverUrl !== "string" || !value.serverUrl || !value.rtcBinding || value.rtcBinding.backend !== value.room.rtcBackend || value.rtcBinding.epoch !== value.room.rtcEpoch || value.rtcBinding.roomName !== expectedRTCRoomName(value.room.id, value.room.rtcEpoch) || value.rtcBinding.serverUrl !== value.serverUrl) {
    throw new Error(ui("The server did not issue complete room credentials.", "Сервер не выдал полные данные для входа в комнату."));
  }
  if (!value.participant?.identity) throw new Error(ui("The server did not issue a participant identity.", "Сервер не назвал участника."));
  if (!value.mediaPolicy || value.mediaPolicy.maxCameraPreviews < 1) throw new Error(ui("The server did not issue a usable media policy.", "Сервер не выдал правила связи."));
  if (value.room.mode === "private_1to1" && !value.mediaSecurity) throw new Error(ui("The server did not issue the mandatory private-call security policy.", "Сервер не выдал правила защиты личного звонка."));
  return value;
}
